import { useMemo } from 'react';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { getOrgDateString } from '@/modules/time-attendance/attendance/services/attendance-rules';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { getEmployeesPresenceFacts } from '../services/employee-presence.service';
import { derivePresence, PresenceView } from '../utils/employee-presence';

/** Statuses are read again every minute while the list is open. */
const PRESENCE_REFRESH_MS = 60 * 1000;

/**
 * Today's presence of each of `userIds` (People › Employees). Pass `enabled: false` for viewers who may
 * not read everyone's attendance (it then runs no query at all).
 */
export const useEmployeesPresence = (userIds: string[], enabled: boolean) => {
  const day = getOrgDateString(new Date());
  const ids = useMemo(() => [...new Set(userIds.filter(Boolean))].sort(), [userIds]);
  const { data, isLoading, dataUpdatedAt } = useGlobalQuery({
    queryKey: ['hr-employee-presence', day, ids],
    queryFn: () => getEmployeesPresenceFacts(ids, day),
    enabled: enabled && ids.length > 0,
    staleTime: PRESENCE_REFRESH_MS,
    refetchInterval: PRESENCE_REFRESH_MS,
  });

  // Worked out again on each refresh, so a break turns "2 h+" without a new query.
  const byUserId = useMemo(() => {
    const now = new Date(dataUpdatedAt || Date.now());
    const timeZone = getOrganizationTimeZone();
    return Object.fromEntries(
      Object.entries(data ?? {}).map(([userId, facts]) => [
        userId,
        derivePresence({ ...facts, now, timeZone }),
      ])
    ) as Record<string, PresenceView>;
  }, [data, dataUpdatedAt]);

  return { byUserId, isLoading: enabled && ids.length > 0 && isLoading };
};
